'use client'

import { useMemo, useRef, useState } from 'react'
import { useFullscreen } from '@/hooks/use-fullscreen'
import { usePrefersReducedMotion } from '@/hooks/use-is-mobile'
import {
  ROUTE,
  arrivalsFor,
  calendarDaysToChristmas,
  computeMagicStats,
  computeTrackerState,
  describeMap,
  interpolatePosition,
  resolveStatusMessage,
  resolveWorkshopActivity,
  seasonFor,
  type SleighPosition,
  type TrackerCopy,
  type TrackerLocale,
  type TrackerState,
} from '@/lib/santa-tracker'
import { buildRoutePath } from '@/lib/santa-tracker/world-map'
import { TrackerCtaSection } from './tracker-cta-section'
import { TrackerEtaCard } from './tracker-eta-card'
import { TrackerHero, type TrackerHeroLetterLink } from './tracker-hero'
import { TrackerMapSection } from './tracker-map-section'
import { TrackerRouteList } from './tracker-route-list'
import { TrackerStats } from './tracker-stats'
import { TrackerWorkshopCard } from './tracker-workshop-card'
import { useSleighBells } from './use-sleigh-bells'
import { useTrackerClock } from './use-tracker-clock'
import { useTrackerInteractions } from './use-tracker-interactions'

/**
 * La experiencia interactiva del tracker: compone las secciones y les pasa el
 * estado que sale del reloj.
 *
 * El reloj vivo está en `useTrackerClock` y los manejadores (selección, sonido,
 * compartir, pantalla completa, analítica) en `useTrackerInteractions`; aquí
 * solo queda derivar el estado y repartirlo.
 *
 * **Privacidad.** No se pide geolocalización en ningún momento. La estimación
 * usa la zona horaria que el navegador ya publica, que da una franja de país,
 * no una dirección.
 */

/** Minutos de estela: posiciones reales de hace un rato, no una línea recta. */
const TRAIL_MINUTES = [4, 9, 16] as const

export interface SantaTrackerExperienceProps {
  copy: TrackerCopy
  locale: TrackerLocale
  /** Instante con el que el servidor renderizó el HTML. */
  initialNowMs: number
  /** Desfase con el que el servidor calculó la estimación (zona por defecto). */
  serverUtcOffsetHours: number
  /** URL canónica, para compartir. */
  shareUrl: string
  authHref: string
  charactersHref: string
  /** La landing «Papá Noel gratis» del locale (enlace bajo el CTA final). */
  freeSantaHref: string
  /**
   * Enlace a la guía de la carta junto a la cuenta atrás. Lo resuelve la
   * página en servidor y solo lo pasa en el locale que tiene la guía (es);
   * en el resto la página no cambia nada.
   */
  letterLink?: TrackerHeroLetterLink
}

/**
 * El trineo sobrevuela la zona del visitante: el reloj cede el paso al aviso
 * de «ya llega». Solo en fase `traveling` y dentro de la ventana local.
 */
function isArrivingNow(state: TrackerState, nowMs: number): boolean {
  return (
    state.phase === 'traveling' &&
    nowMs >= state.visitorWindow.startMs &&
    nowMs <= state.visitorWindow.endMs
  )
}

export function SantaTrackerExperience({
  copy,
  locale,
  initialNowMs,
  serverUtcOffsetHours,
  shareUrl,
  authHref,
  charactersHref,
  freeSantaHref,
  letterLink,
}: SantaTrackerExperienceProps) {
  const clock = useTrackerClock(initialNowMs, serverUtcOffsetHours)
  const { nowMs, utcOffsetHours, timeZoneName } = clock

  const [follow, setFollow] = useState(false)
  const [motionOverride, setMotionOverride] = useState<boolean | null>(null)

  const mapRef = useRef<HTMLDivElement>(null)
  const fullscreen = useFullscreen(mapRef)
  const systemReducedMotion = usePrefersReducedMotion()

  // El ajuste del sistema manda mientras el usuario no toque el control de la
  // página; a partir de ahí manda su elección explícita.
  const animate = motionOverride ?? !systemReducedMotion

  const state = useMemo(
    () => computeTrackerState({ nowMs, utcOffsetHours }),
    [nowMs, utcOffsetHours]
  )

  const interactions = useTrackerInteractions({ copy, locale, state, shareUrl, fullscreen })
  useSleighBells(interactions.soundOn)

  const stats = useMemo(() => computeMagicStats(state), [state])
  const statusMessage = useMemo(() => resolveStatusMessage(state, copy), [state, copy])
  const workshopActivity = useMemo(() => resolveWorkshopActivity(state, copy), [state, copy])
  const mapDescription = useMemo(() => describeMap(state, copy), [state, copy])

  const arrivals = useMemo(() => arrivalsFor(state.season), [state.season])

  const trail = useMemo<SleighPosition[]>(() => {
    if (state.phase !== 'traveling') return []
    return TRAIL_MINUTES.map(
      (minutes) =>
        interpolatePosition(arrivals, { nowMs: nowMs - minutes * 60_000, utcOffsetHours }).position
    )
  }, [arrivals, nowMs, utcOffsetHours, state.phase])

  const visitedPath = useMemo(() => {
    if (state.stopIndex < 0) return ''
    const points = ROUTE.slice(0, state.stopIndex + 1).map((stop) => ({
      lon: stop.lon,
      lat: stop.lat,
    }))
    points.push(state.position)
    return buildRoutePath(points)
  }, [state.stopIndex, state.position])

  const daysToChristmas = calendarDaysToChristmas(
    { nowMs, utcOffsetHours },
    state.phase === 'completed' ? seasonFor(state.season.year + 1) : state.season
  )

  const arriving = isArrivingNow(state, nowMs)

  return (
    <>
      <TrackerHero
        copy={copy}
        locale={locale}
        state={state}
        statusMessage={statusMessage}
        daysToChristmas={daysToChristmas}
        arriving={arriving}
        letterLink={letterLink}
      />

      <TrackerMapSection
        copy={copy}
        locale={locale}
        state={state}
        trail={trail}
        visitedPath={visitedPath}
        selection={interactions.selection}
        statusMessage={statusMessage}
        mapDescription={mapDescription}
        follow={follow}
        animate={animate}
        auroraAwake={interactions.auroraAwake}
        mapRef={mapRef}
        controls={{
          copy,
          soundOn: interactions.soundOn,
          onToggleSound: interactions.toggleSound,
          fullscreenSupported: fullscreen.isSupported,
          isFullscreen: fullscreen.isFullscreen,
          onToggleFullscreen: interactions.toggleFullscreen,
          onShare: interactions.share,
          shareFeedback: interactions.shareFeedback,
          follow,
          onToggleFollow: () => setFollow((current) => !current),
          motionOn: animate,
          onToggleMotion: () => setMotionOverride(!animate),
        }}
        onSelectStop={interactions.selectStop}
        onSelectSleigh={interactions.selectSleigh}
        onTapNorthPole={interactions.tapNorthPole}
        onCloseDetail={interactions.clearSelection}
      />

      <TrackerEtaCard
        copy={copy}
        locale={locale}
        state={state}
        utcOffsetHours={utcOffsetHours}
        timeZoneName={timeZoneName}
      />

      <TrackerStats copy={copy} locale={locale} phase={state.phase} stats={stats} />

      <TrackerWorkshopCard copy={copy} activity={workshopActivity} />

      <TrackerRouteList
        copy={copy}
        locale={locale}
        state={state}
        onSelectStop={interactions.selectStop}
      />

      <TrackerCtaSection
        copy={copy}
        phase={state.phase}
        authHref={authHref}
        charactersHref={charactersHref}
        freeSantaHref={freeSantaHref}
        onCtaClick={interactions.trackCtaClick}
      />
    </>
  )
}
